/**
 * Reports contracts (M5 task 9; 03-modules.md §9, 04-data-model.md "reports", 05-api.md
 * "Reports"): the five outlet reports (sales, items, tax, payments and settlement, voids and
 * discounts), the "today" dashboard, the on-demand rebuild of a day's figures, and the event
 * the rebuild leaves.
 *
 * Money is integer paise; quantities are integer milli-units (1000 = one). Every report
 * answers for one outlet and a business-date range, and says how final each day's figures
 * are (`finality`): a day closed by hand without force is `final`; a day still open is
 * `live`; a day closed automatically (`day.close_mode = clock`) or by force (over unsettled
 * orders), or changed after it was final, is `provisional` and keeps following the orders
 * (accounting README, "Hazard").
 */
import { aggregatorPayoutsSchema } from './aggregators.js';
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { paiseSchema } from './money.js';
import { PAYMENT_METHOD_KINDS } from './org.js';
import type { PermissionKey } from './permissions.js';

// ---------------------------------------------------------------------------
// Which report, which format, which permission

export const REPORT_KEYS = ['sales', 'items', 'tax', 'payments', 'voids-discounts'] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

export const REPORT_FORMATS = ['json', 'csv', 'xlsx', 'pdf'] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];

/** The permission each report needs (a non-JSON format needs `reports.export` as well). */
export const REPORT_PERMISSIONS = {
  sales: 'reports.view.sales',
  items: 'reports.view.items',
  tax: 'reports.view.tax',
  payments: 'reports.view.payments',
  'voids-discounts': 'reports.view.voids_discounts',
} as const satisfies Record<ReportKey, PermissionKey>;

/** The longest range one report answers for (one year plus a leap day). */
export const MAX_REPORT_DAYS = 366;

export const SALES_GROUP_BY = [
  'day',
  'hour',
  'order_type',
  'channel',
  'source',
  'section',
] as const;
export type SalesGroupBy = (typeof SALES_GROUP_BY)[number];

export const ITEMS_GROUP_BY = ['product', 'category', 'day'] as const;
export type ItemsGroupBy = (typeof ITEMS_GROUP_BY)[number];

const queryInt = (v: unknown) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);

/** `?from=&to=&groupBy=&format=` (business dates, both inclusive). */
export const reportQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  groupBy: z.string().min(1).max(32).optional(),
  format: z.enum(REPORT_FORMATS).default('json'),
  /** Voids and discounts only: the page of entries (default 500). */
  limit: z.preprocess(queryInt, z.number().int().min(1).max(5000).optional()),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).optional()),
});
export type ReportQuery = z.output<typeof reportQuerySchema>;

export const reportParamsSchema = z.object({ outletId: uuidSchema });

// ---------------------------------------------------------------------------
// Finality

export const DAY_FINALITY = ['live', 'provisional', 'final'] as const;
export type DayFinality = (typeof DAY_FINALITY)[number];

export const dayFinalitySchema = z.object({
  date: businessDateSchema,
  state: z.enum(DAY_FINALITY),
  /** How the day was closed (`manual`, `auto`), null while it is open. */
  closeKind: z.string().nullable(),
  forced: z.boolean(),
  /** Orders of the day that were still unsettled when it was last checked. */
  unsettledOrders: z.int(),
  /** When the day's figures were last rebuilt from the orders, null: never (events only). */
  rebuiltAt: isoTimestampSchema.nullable(),
});
export type DayFinalityView = z.output<typeof dayFinalitySchema>;

/** Every report says how final its days are; `state` is the least final of them. */
export const finalitySchema = z.object({
  state: z.enum(DAY_FINALITY),
  days: z.array(dayFinalitySchema),
});
export type Finality = z.output<typeof finalitySchema>;

const reportHead = {
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  generatedAt: isoTimestampSchema,
  finality: finalitySchema,
};

// ---------------------------------------------------------------------------
// 1. Sales

export const salesFiguresSchema = z.object({
  orders: z.int(),
  covers: z.int(),
  /** Before discount, pre-tax, charges excluded (`order.subtotal_paise`). */
  grossPaise: paiseSchema,
  discountPaise: paiseSchema,
  chargesPaise: paiseSchema,
  taxPaise: paiseSchema,
  roundOffPaise: paiseSchema,
  /** The bill total: gross - discount + charges + tax + round-off. */
  netPaise: paiseSchema,
  /** Net per order (rounded half up); 0 without orders. */
  avgOrderPaise: paiseSchema,
  /** Net per cover (APC, rounded half up); 0 without covers. */
  avgPerCoverPaise: paiseSchema,
});
export type SalesFigures = z.output<typeof salesFiguresSchema>;

export const salesRowSchema = salesFiguresSchema.extend({
  key: z.string(),
  label: z.string(),
});
export type SalesRow = z.output<typeof salesRowSchema>;

export const salesReportSchema = z.object({
  ...reportHead,
  groupBy: z.enum(SALES_GROUP_BY),
  rows: z.array(salesRowSchema),
  totals: salesFiguresSchema,
});
export type SalesReport = z.output<typeof salesReportSchema>;

// ---------------------------------------------------------------------------
// 2. Items

export const itemFiguresSchema = z.object({
  /** Billed quantity (not complimentary, not voided). */
  qtyMilli: z.int(),
  /** Pre-tax value before discount of the billed quantity. */
  grossPaise: paiseSchema,
  discountPaise: paiseSchema,
  /** gross - discount. */
  netPaise: paiseSchema,
  compQtyMilli: z.int(),
  /** Menu value of the complimentary quantity. */
  compValuePaise: paiseSchema,
  voidQtyMilli: z.int(),
  /** Menu value of the voided quantity (the item itself, modifiers not included). */
  voidValuePaise: paiseSchema,
});
export type ItemFigures = z.output<typeof itemFiguresSchema>;

export const itemRowSchema = itemFiguresSchema.extend({
  key: z.string(),
  label: z.string(),
  productId: uuidSchema.nullable(),
  variantId: uuidSchema.nullable(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
});
export type ItemRow = z.output<typeof itemRowSchema>;

export const itemsReportSchema = z.object({
  ...reportHead,
  groupBy: z.enum(ITEMS_GROUP_BY),
  rows: z.array(itemRowSchema),
  totals: itemFiguresSchema,
});
export type ItemsReport = z.output<typeof itemsReportSchema>;

// ---------------------------------------------------------------------------
// 3. Tax

export const REPORT_TAX_COMPONENTS = ['cgst', 'sgst', 'igst', 'cess'] as const;

export const taxRateRowSchema = z.object({
  /** The combined GST rate of the supply (CGST + SGST, or IGST), in basis points. */
  rateBp: z.int(),
  taxablePaise: paiseSchema,
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  taxPaise: paiseSchema,
});
export type TaxRateRow = z.output<typeof taxRateRowSchema>;

export const taxComponentRowSchema = z.object({
  component: z.enum(REPORT_TAX_COMPONENTS),
  rateBp: z.int(),
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
});
export type TaxComponentRow = z.output<typeof taxComponentRowSchema>;

export const invoiceDayRowSchema = z.object({
  date: businessDateSchema,
  count: z.int(),
  /** First and last invoice number of the day as printed (`IND/2627/15`), null when none. */
  firstNo: z.string().nullable(),
  lastNo: z.string().nullable(),
  totalPaise: paiseSchema,
  taxPaise: paiseSchema,
});
export type InvoiceDayRow = z.output<typeof invoiceDayRowSchema>;

export const b2bInvoiceRowSchema = z.object({
  invoiceId: uuidSchema,
  invoiceNo: z.string(),
  date: businessDateSchema,
  buyerGstin: z.string(),
  buyerName: z.string().nullable(),
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
  totalPaise: paiseSchema,
});
export type B2bInvoiceRow = z.output<typeof b2bInvoiceRowSchema>;

export const taxReportSchema = z.object({
  ...reportHead,
  /** One row per combined GST rate, with its components (cess is not a GST rate: see totals). */
  rates: z.array(taxRateRowSchema),
  /** One row per component and rate (what `daily_tax_agg` holds). */
  components: z.array(taxComponentRowSchema),
  totals: z.object({
    taxablePaise: paiseSchema,
    /** GST (CGST + SGST + IGST). */
    gstPaise: paiseSchema,
    cessPaise: paiseSchema,
    taxPaise: paiseSchema,
  }),
  invoices: z.object({
    count: z.int(),
    totalPaise: paiseSchema,
    taxPaise: paiseSchema,
    firstNo: z.string().nullable(),
    lastNo: z.string().nullable(),
    days: z.array(invoiceDayRowSchema),
  }),
  /** Invoices issued to a business buyer (customer GSTIN), oldest first. */
  b2b: z.array(b2bInvoiceRowSchema),
});
export type TaxReport = z.output<typeof taxReportSchema>;

// ---------------------------------------------------------------------------
// 4. Payments and settlement

export const paymentModeRowSchema = z.object({
  methodKind: z.enum(PAYMENT_METHOD_KINDS),
  label: z.string(),
  count: z.int(),
  /** Applied to bills (tips and change excluded). */
  amountPaise: paiseSchema,
  tipPaise: paiseSchema,
});
export type PaymentModeRow = z.output<typeof paymentModeRowSchema>;

export const pendingSettlementRowSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  reasonCode: z.string(),
  markedAt: isoTimestampSchema,
});
export type PendingSettlementRow = z.output<typeof pendingSettlementRowSchema>;

export const paymentCorrectionRowSchema = z.object({
  id: uuidSchema,
  at: isoTimestampSchema,
  orderId: uuidSchema,
  orderNo: z.int().nullable(),
  businessDate: businessDateSchema.nullable(),
  fromKind: z.enum(PAYMENT_METHOD_KINDS),
  toKind: z.enum(PAYMENT_METHOD_KINDS),
  fromAmountPaise: paiseSchema,
  toAmountPaise: paiseSchema,
  reasonCode: z.string(),
  actorName: z.string().nullable(),
  approverName: z.string().nullable(),
});
export type PaymentCorrectionRow = z.output<typeof paymentCorrectionRowSchema>;

export const paymentsReportSchema = z.object({
  ...reportHead,
  modes: z.array(paymentModeRowSchema),
  totals: z.object({ count: z.int(), amountPaise: paiseSchema, tipPaise: paiseSchema }),
  /** Orders parked to settle later: where they stand now, whatever the dates asked for. */
  pendingSettlements: z.object({
    asOf: isoTimestampSchema,
    count: z.int(),
    duePaise: paiseSchema,
    rows: z.array(pendingSettlementRowSchema),
  }),
  /** Payment-mode corrections made on tenders of the dates asked for. */
  corrections: z.array(paymentCorrectionRowSchema),
  /**
   * Aggregator payouts vs orders (03-modules.md §9 report 4): per platform, the accepted Swiggy and
   * Zomato orders of the dates asked for against what imported payout files paid, with the discount
   * split the platform deducted. Empty when the outlet has no platform orders or payouts in the range.
   */
  aggregatorPayouts: aggregatorPayoutsSchema,
});
export type PaymentsReport = z.output<typeof paymentsReportSchema>;

// ---------------------------------------------------------------------------
// 5. Voids and discounts

export const EXCEPTION_KINDS = ['void', 'cancel', 'reject', 'discount', 'complimentary'] as const;
export type ExceptionKind = (typeof EXCEPTION_KINDS)[number];

export const exceptionRowSchema = z.object({
  id: uuidSchema,
  kind: z.enum(EXCEPTION_KINDS),
  businessDate: businessDateSchema,
  at: isoTimestampSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  /** The voided or complimentary item; null for whole-order entries. */
  itemName: z.string().nullable(),
  qtyMilli: z.int().nullable(),
  /** Menu value voided or given away, the discount as applied, or the bill cancelled. */
  amountPaise: paiseSchema,
  reasonCode: z.string().nullable(),
  note: z.string().nullable(),
  /** The discount's name when it has one. */
  label: z.string().nullable(),
  actorId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  approverId: uuidSchema.nullable(),
  approverName: z.string().nullable(),
});
export type ExceptionRow = z.output<typeof exceptionRowSchema>;

export const exceptionSummarySchema = z.object({
  kind: z.enum(EXCEPTION_KINDS),
  count: z.int(),
  amountPaise: paiseSchema,
});
export type ExceptionSummary = z.output<typeof exceptionSummarySchema>;

export const voidsDiscountsReportSchema = z.object({
  ...reportHead,
  summary: z.array(exceptionSummarySchema),
  total: z.int(),
  limit: z.int(),
  offset: z.int(),
  rows: z.array(exceptionRowSchema),
});
export type VoidsDiscountsReport = z.output<typeof voidsDiscountsReportSchema>;

// ---------------------------------------------------------------------------
// Dashboard "today"

export const dashboardSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  businessDate: businessDateSchema,
  generatedAt: isoTimestampSchema,
  /** When the figures were last brought up to date from the orders (events lag by seconds). */
  asOf: isoTimestampSchema.nullable(),
  finality: dayFinalitySchema,
  sales: salesFiguresSchema,
  byHour: z.array(
    z.object({ hour: z.int().min(0).max(23), orders: z.int(), netPaise: paiseSchema }),
  ),
  byMode: z.array(paymentModeRowSchema),
  topItems: z.array(z.object({ label: z.string(), qtyMilli: z.int(), netPaise: paiseSchema })),
  /** Voids, cancels, rejections, discounts and complimentaries so far. */
  exceptions: z.array(exceptionSummarySchema),
  /** Orders running or awaiting payment right now (from the orders themselves). */
  openOrders: z.int(),
});
export type Dashboard = z.output<typeof dashboardSchema>;

// ---------------------------------------------------------------------------
// Rebuild

export const MAX_REBUILD_DAYS = 31;

/** `POST {o}/reports/rebuild`: recompute these business dates from the orders (at most 31). */
export const rebuildReportsSchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
});
export type RebuildReportsInput = z.output<typeof rebuildReportsSchema>;

export const rebuildResultSchema = z.object({
  days: z.array(
    z.object({
      date: businessDateSchema,
      orders: z.int(),
      /** Aggregate rows that were wrong, missing or extra before the rebuild (0: already right). */
      changed: z.int(),
      state: z.enum(DAY_FINALITY),
    }),
  ),
});
export type RebuildResult = z.output<typeof rebuildResultSchema>;

// ---------------------------------------------------------------------------
// Events

export const reportDayRebuiltV1 = defineEvent(
  'report.day_rebuilt',
  1,
  z.object({
    businessDate: businessDateSchema,
    orders: z.int(),
    changed: z.int(),
    state: z.enum(DAY_FINALITY),
  }),
);

export const REPORT_EVENTS = [reportDayRebuiltV1] as const;
